import React, {useState, useEffect} from "react";
import {Button, Table, Badge, Input} from "reactstrap";
import {DatePicker} from "antd";
import {toastr} from 'react-redux-toastr';
import general_fetch from '../../utils/fetch';
import {CspModal, CspModalBody, CspModalFooter, CspModalHeader} from "../CspModal";

// Backend: POST csp/account_statement (customer_success_admin only). Body: customer_store_id and either a
// range ('last_30_days' | 'last_quarter' | 'last_year') or explicit start_date / end_date; with neither,
// the last quarter ending now. Response: {start_date, end_date, opening_balance, closing_balance,
// totals_by_kind, transactions: [{id, created_at, kind, signed_credits, balance_after, comment, bill_id,
// order_id, order_type, tie_unresolved}]}.
// Payment cycles come from billing/get_bills (customer_store_id + year): every bill of a cycle shares a
// payment_cycle_index. A cycle's window opens at its first bill's start_date. The cycle's own due is
// minted and settled AFTER its last bill's end_date (the usage debits, the credit notes and the
// settlement all post then), so the window closes at the latest completed payment on the cycle's final
// orders (each bill carries order.payments), or at the bill end if that is later; a cycle still due
// closes at now.
const MODES = [
	{key: 'cycle', label: 'Payment cycle'},
	{key: 'last_30_days', label: 'Last 30 days'},
	{key: 'last_quarter', label: 'Last quarter'},
	{key: 'last_year', label: 'Last year'},
	{key: 'custom', label: 'Custom dates'},
];
const CYCLE_YEARS_BACK = 3; // bills are fetched per year; older cycles are reachable through custom dates

const KIND_LABELS = {
	settlement_payment: 'Payment received',
	settlement_forward_charge: 'Forward charge on settlement',
	credit_note: 'Credit note',
	credit_note_reversal: 'Credit note reversal',
	credit_note_removal: 'Credit note removed',
	bill_usage_debit: 'Usage debited on bill',
	licence_debit: 'Licence debit',
	balance_clear: 'Balance cleared into due',
	bill_rollback: 'Bill rolled back',
	excess_user_topup: 'Extra user top-up',
	free_credits: 'Free credits',
	grace_completion: 'Grace completion',
	org_move: 'Org move',
	refund: 'Refund',
	other: 'Other',
};
const kind_label = (kind) => KIND_LABELS[kind] || kind;

const fmt = (n) => (n === null || n === undefined || n === '' ? '-' : Number(n).toLocaleString('en-IN', {maximumFractionDigits: 4}));
const fmt_day = (d) => (d ? new Date(d).toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'}) : '-');
const fmt_date = (d) => (d ? new Date(d).toLocaleString('en-IN', {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false}) : '-');

// Group an org's bills into payment cycles, newest first. Each cycle carries start_date / end_date (the
// bills' span), first_paid_at / settled_at (earliest and latest completed payment on its final orders,
// or null) and the statement window: window_start opens at the first payment when that precedes the
// bills (the onboarding cycle and other prepaid orders are paid before their bill), window_end closes
// as described above.
export const cycles_from_bills = (bills, now = new Date()) => {
	const by_index = {};
	for (const b of bills || []) {
		if (b.payment_cycle_index === null || b.payment_cycle_index === undefined) continue;
		const k = String(b.payment_cycle_index);
		if (!by_index[k]) by_index[k] = {index: Number(b.payment_cycle_index), start_date: b.start_date, end_date: b.end_date, first_paid_at: null, settled_at: null, bills: 0, statuses: new Set(), has_payment_cycle_bill: false};
		const c = by_index[k];
		if (new Date(b.start_date) < new Date(c.start_date)) c.start_date = b.start_date;
		if (new Date(b.end_date) > new Date(c.end_date)) c.end_date = b.end_date;
		c.bills += 1;
		c.statuses.add(b.status);
		if (b.is_payment_cycle) c.has_payment_cycle_bill = true;
		for (const p of (b.order && b.order.payments) || []) {
			if (p.status && p.status !== 'completed') continue;
			if (!c.first_paid_at || new Date(p.created_at) < new Date(c.first_paid_at)) c.first_paid_at = p.created_at;
			if (!c.settled_at || new Date(p.created_at) > new Date(c.settled_at)) c.settled_at = p.created_at;
		}
	}
	return Object.values(by_index)
		.map(c => {
			const status = c.statuses.has('due') ? 'due' : (c.statuses.has('cleared') && c.statuses.size === 1 ? 'cleared' : [...c.statuses].join('/'));
			let window_start = c.start_date;
			if (c.first_paid_at && new Date(c.first_paid_at) < new Date(window_start)) window_start = c.first_paid_at;
			let window_end = c.end_date;
			if (c.settled_at && new Date(c.settled_at) > new Date(window_end)) window_end = c.settled_at;
			if (status === 'due' && new Date(now) > new Date(window_end)) window_end = now.toISOString();
			return {...c, status, window_start, window_end};
		})
		.sort((a, b) => b.index - a.index);
};

const AccountStatementModal = ({open, toggle, store_id, store_info, set_page_loader}) => {
	const [mode, set_mode] = useState('cycle');
	const [cycles, set_cycles] = useState([]);
	const [cycle_index, set_cycle_index] = useState('');
	const [cycles_loading, set_cycles_loading] = useState(false);
	const [start_date, set_start_date] = useState();
	const [end_date, set_end_date] = useState();
	const [statement, set_statement] = useState(null);
	const [error, set_error] = useState('');
	const currency = store_info && store_info.ccr_details ? store_info.ccr_details.currency : '';
	const cost_per_credit = store_info && store_info.ccr_details ? Number(store_info.ccr_details.cost_per_credit) : null;

	const load_cycles = async () => {
		set_cycles_loading(true);
		try {
			const this_year = new Date().getFullYear();
			const years = Array.from({length: CYCLE_YEARS_BACK}, (_, i) => this_year - i);
			const per_year = await Promise.all(years.map(year => general_fetch({url: 'billing/get_bills', body: {customer_store_id: store_id, year}}).catch(() => [])));
			const found = cycles_from_bills(per_year.flat());
			set_cycles(found);
			return found;
		} catch (e) {
			console.error(e);
			set_cycles([]);
			return [];
		} finally {
			set_cycles_loading(false);
		}
	};

	const fetch_statement = async (override = {}) => {
		try {
			const use_mode = override.mode || mode;
			const use_cycle = override.cycle_index !== undefined ? override.cycle_index : cycle_index;
			let body = {customer_store_id: store_id};
			if (use_mode === 'cycle') {
				const cycle = cycles.find(c => String(c.index) === String(use_cycle)) || (override.cycles || []).find(c => String(c.index) === String(use_cycle));
				if (!cycle) { set_error('Pick a payment cycle'); return; }
				body.start_date = cycle.window_start;
				body.end_date = cycle.window_end;
			} else if (use_mode === 'custom') {
				if (!start_date || !end_date) { set_error('Pick both dates'); return; }
				body.start_date = start_date;
				body.end_date = end_date;
			} else {
				body.range = use_mode;
			}
			set_error('');
			set_page_loader(true, 'Fetching account statement...');
			const resp = await general_fetch({url: 'csp/account_statement', body});
			set_page_loader(false);
			set_statement(resp);
		} catch (e) {
			set_page_loader(false);
			console.error(e);
			const msg = e && e.resp && e.resp.message ? e.resp.message : "Couldn't fetch the account statement";
			set_error(msg);
			set_statement(null);
			toastr.error(msg);
		}
	};

	useEffect(() => {
		if (!open) return;
		set_statement(null);
		set_error('');
		(async () => {
			const found = await load_cycles();
			if (found.length) {
				set_mode('cycle');
				set_cycle_index(String(found[0].index));
				fetch_statement({mode: 'cycle', cycle_index: String(found[0].index), cycles: found});
			} else {
				set_mode('last_quarter');
				fetch_statement({mode: 'last_quarter'});
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// A new selection drops the previous result at once, so the window shown is never the old one's.
	// Preset ranges and a cycle choice need nothing more from CS, so they fetch on selection; custom
	// dates wait for Fetch.
	const choose_mode = (key) => {
		set_mode(key);
		set_statement(null);
		set_error('');
		if (key === 'cycle' && cycle_index) fetch_statement({mode: 'cycle', cycle_index});
		else if (key !== 'cycle' && key !== 'custom') fetch_statement({mode: key});
	};
	const choose_cycle = (index) => {
		set_cycle_index(index);
		set_statement(null);
		set_error('');
		fetch_statement({mode: 'cycle', cycle_index: index});
	};

	const onclose = () => { toggle(); set_statement(null); set_error(''); };
	const kinds_present = statement && statement.totals_by_kind ? Object.entries(statement.totals_by_kind).filter(([k, v]) => v && v.count) : [];
	const net = statement ? Number(statement.closing_balance) - Number(statement.opening_balance) : 0;
	const selected_cycle = cycles.find(c => String(c.index) === String(cycle_index));

	return (
		<CspModal open={open} toggle={onclose} size="xl">
			<CspModalHeader title={'Account Statement'} toggle={onclose}/>
			<CspModalBody style={{maxHeight: '70vh', overflowY: 'auto', fontSize: '13px'}}>
				<div className="flex_property mb-2" style={{gap: '8px', flexWrap: 'wrap'}}>
					{MODES.map(m => (
						<Button key={m.key} size="sm" color={mode === m.key ? 'primary' : 'secondary'} outline={mode !== m.key} onClick={() => choose_mode(m.key)} disabled={m.key === 'cycle' && !cycles.length && !cycles_loading}>{m.label}</Button>
					))}
				</div>
				<div className="flex_property mb-3" style={{gap: '8px', flexWrap: 'wrap', alignItems: 'center'}}>
					{mode === 'cycle' ? (
						cycles_loading ? <span style={{color: '#64748b'}}>Loading payment cycles...</span> : (
							cycles.length ? (
								<Input type="select" bsSize="sm" style={{maxWidth: '420px'}} value={cycle_index} onChange={e => choose_cycle(e.target.value)}>
									{cycles.map(c => (
										<option key={c.index} value={String(c.index)}>Cycle {c.index}: {fmt_day(c.start_date)} to {fmt_day(c.end_date)} ({c.bills} bill{c.bills === 1 ? '' : 's'}, {c.status}{c.settled_at ? `, settled ${fmt_day(c.settled_at)}` : ''})</option>
									))}
								</Input>
							) : <span style={{color: '#64748b'}}>No payment cycles in the last {CYCLE_YEARS_BACK} years; use another option.</span>
						)
					) : ''}
					{mode === 'custom' ? (
						<>
							<DatePicker format="YYYY-MM-DD" placeholder="From" onChange={(d, s) => set_start_date(s)}/>
							<DatePicker format="YYYY-MM-DD" placeholder="To" onChange={(d, s) => set_end_date(s)} disabledDate={(d) => start_date ? new Date(d) < new Date(start_date) : false}/>
						</>
					) : ''}
					<Button size="sm" color="primary" onClick={() => fetch_statement()}>Fetch</Button>
				</div>
				{error ? <div className="mb-2" style={{color: '#fb6340'}}>{error}</div> : ''}
				{statement ? (
					<>
						<div className="mb-2" style={{color: '#64748b'}}>
							{mode === 'cycle' && selected_cycle ? <span>Payment cycle <b>{selected_cycle.index}</b>{selected_cycle.has_payment_cycle_bill ? '' : ' (no payment-cycle bill yet)'}, </span> : ''}
							window <b>{fmt_date(statement.start_date)}</b> to <b>{fmt_date(statement.end_date)}</b>
							{mode === 'cycle' && selected_cycle && (selected_cycle.window_start !== selected_cycle.start_date || selected_cycle.window_end !== selected_cycle.end_date) ? (
								<span> (bills {fmt_day(selected_cycle.start_date)} to {fmt_day(selected_cycle.end_date)}
									{selected_cycle.window_start !== selected_cycle.start_date ? '; opened at the upfront payment' : ''}
									{selected_cycle.window_end !== selected_cycle.end_date ? (selected_cycle.settled_at && selected_cycle.window_end === selected_cycle.settled_at ? '; extended to the settlement' : '; still due, extended to now') : ''})</span>
							) : ''}
						</div>
						<div className="flex_property mb-3" style={{gap: '24px', flexWrap: 'wrap'}}>
							<div>Opening balance: <b>{fmt(statement.opening_balance)}</b> credits</div>
							<div>Net movement: <b style={{color: net < 0 ? '#fb6340' : '#2dce89'}}>{net >= 0 ? '+' : ''}{fmt(net)}</b> credits</div>
							<div>Closing balance: <b>{fmt(statement.closing_balance)}</b> credits{cost_per_credit ? <span style={{color: '#64748b'}}> ({currency} {fmt(Number(statement.closing_balance) * cost_per_credit)})</span> : ''}</div>
						</div>
						{kinds_present.length ? (
							<div className="mb-3">
								<div className="mb-1" style={{fontWeight: 700}}>Totals by kind</div>
								<Table size="sm" responsive className="mb-0">
									<thead><tr><th>Kind</th><th className="text-right">Count</th><th className="text-right">Credited</th><th className="text-right">Debited</th></tr></thead>
									<tbody>
										{kinds_present.map(([k, v]) => (
											<tr key={k}><td>{kind_label(k)}</td><td className="text-right">{v.count}</td><td className="text-right">{fmt(v.credited)}</td><td className="text-right">{fmt(v.debited)}</td></tr>
										))}
									</tbody>
								</Table>
							</div>
						) : ''}
						<div className="mb-1" style={{fontWeight: 700}}>Transactions ({statement.transactions ? statement.transactions.length : 0})</div>
						{statement.transactions && statement.transactions.length ? (
							<Table size="sm" responsive className="mb-0">
								<thead><tr><th>Date</th><th>Kind</th><th>Comment</th><th>Due / bill</th><th className="text-right">Credits</th><th className="text-right">Balance after</th></tr></thead>
								<tbody>
									{statement.transactions.map(t => (
										<tr key={t.id}>
											<td style={{whiteSpace: 'nowrap'}}>{fmt_date(t.created_at)}{t.tie_unresolved ? <Badge color="warning" className="ml-1" title="Rows of this second could not be ordered by their balance chain">tie</Badge> : ''}</td>
											<td style={{whiteSpace: 'nowrap'}}>{kind_label(t.kind)}</td>
											<td style={{minWidth: '280px', maxWidth: '420px', whiteSpace: 'normal', wordBreak: 'break-word'}}>{t.comment || '-'}</td>
											<td style={{whiteSpace: 'nowrap', color: '#64748b'}}>{t.order_id ? `due ${t.order_id}` : ''}{t.order_id && t.bill_id ? ' / ' : ''}{t.bill_id ? `bill ${t.bill_id}` : ''}</td>
											<td className="text-right" style={{color: Number(t.signed_credits) < 0 ? '#fb6340' : '#2dce89', whiteSpace: 'nowrap'}}>{Number(t.signed_credits) >= 0 ? '+' : ''}{fmt(t.signed_credits)}</td>
											<td className="text-right" style={{whiteSpace: 'nowrap'}}>{fmt(t.balance_after)}</td>
										</tr>
									))}
								</tbody>
							</Table>
						) : <div style={{color: '#64748b'}}>No ledger rows in this window.</div>}
					</>
				) : (!error ? <div style={{color: '#64748b'}}>Loading...</div> : '')}
			</CspModalBody>
			<CspModalFooter>
				<Button color="secondary" onClick={onclose}>Close</Button>
			</CspModalFooter>
		</CspModal>
	);
};

export default AccountStatementModal;
